/**
 * Note Service
 * Handles all note/communication log API calls including CRUD operations,
 * filtering by type, and managing encrypted notes
 */

import { apiClient, buildQueryParams, ApiResponse } from './api.client';
import { unwrapListResponse } from './api.unwrap';
import {
  Note,
  CreateNoteRequest,
  UpdateNoteRequest,
  ListNotesRequest,
  PaginatedResponse,
  NoteType,
} from '@creatoros/shared';

/**
 * Note statistics interface
 */
export interface NoteStatistics {
  total: number;
  byType: Record<NoteType, number>;
  bySentiment: Record<'positive' | 'neutral' | 'negative', number>;
  recentActivity: {
    date: string;
    count: number;
  }[];
}

/**
 * Note Service Class
 */
class NoteService {
  private readonly basePath = '/api/notes';

  /**
   * Get all notes with optional filtering and pagination
   * @param params - List parameters including filters and pagination
   * @returns Paginated list of notes
   */
  async list(params: ListNotesRequest): Promise<PaginatedResponse<Note>> {
    const queryParams = buildQueryParams(params as unknown as Record<string, unknown>);

    const response = await apiClient.get(this.basePath, {
      params: queryParams,
    });

    return unwrapListResponse<Note>(response.data) as unknown as PaginatedResponse<Note>;
  }

  /**
   * Get a single note by ID
   * @param id - Note ID
   * @returns Note data
   */
  async getById(id: string): Promise<Note> {
    const response = await apiClient.get<ApiResponse<Note>>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch note');
  }

  /**
   * Create a new note
   * @param data - Note creation data
   * @returns Created note
   */
  async create(data: CreateNoteRequest): Promise<Note> {
    const response = await apiClient.post<ApiResponse<Note>>(this.basePath, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to create note');
  }

  /**
   * Update an existing note
   * @param id - Note ID
   * @param data - Note update data
   * @returns Updated note
   */
  async update(id: string, data: UpdateNoteRequest): Promise<Note> {
    const response = await apiClient.patch<ApiResponse<Note>>(`${this.basePath}/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update note');
  }

  /**
   * Delete a note
   * @param id - Note ID
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);
  }

  /**
   * Get notes by deal
   * @param dealId - Deal ID to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of notes for specified deal
   */
  async getByDeal(
    dealId: string,
    params?: Omit<ListNotesRequest, 'dealId'>
  ): Promise<PaginatedResponse<Note>> {
    return this.list({
      ...params,
      dealId,
    });
  }

  /**
   * Get notes by type
   * @param dealId - Deal ID to filter by
   * @param noteType - Note type to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of notes with specified type
   */
  async getByType(
    dealId: string,
    noteType: NoteType,
    params?: Omit<ListNotesRequest, 'dealId' | 'noteType'>
  ): Promise<PaginatedResponse<Note>> {
    return this.list({
      ...params,
      dealId,
      noteType,
    });
  }

  /**
   * Search notes by content
   * @param dealId - Deal ID to search within
   * @param query - Search query string
   * @param params - Additional list parameters
   * @returns Paginated list of matching notes
   */
  async search(
    dealId: string,
    query: string,
    params?: Omit<ListNotesRequest, 'dealId'>
  ): Promise<PaginatedResponse<Note>> {
    const queryParams = buildQueryParams({
      ...params,
      dealId,
      search: query,
    } as Record<string, unknown>);

    const response = await apiClient.get<PaginatedResponse<Note>>(`${this.basePath}/search`, {
      params: queryParams,
    });

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to search notes');
  }

  /**
   * Get encrypted notes for a deal
   * @param dealId - Deal ID
   * @param params - Additional list parameters
   * @returns Paginated list of encrypted notes
   */
  async getEncryptedNotes(
    dealId: string,
    params?: Omit<ListNotesRequest, 'dealId'>
  ): Promise<PaginatedResponse<Note>> {
    const queryParams = buildQueryParams({
      ...params,
      dealId,
      isEncrypted: true,
    } as Record<string, unknown>);

    const response = await apiClient.get<PaginatedResponse<Note>>(this.basePath, {
      params: queryParams,
    });

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to fetch encrypted notes');
  }

  /**
   * Get recent notes across all deals
   * @param limit - Number of recent notes to retrieve
   * @returns List of recent notes
   */
  async getRecent(limit: number = 10): Promise<Note[]> {
    const response = await apiClient.get<ApiResponse<Note[]>>(`${this.basePath}/recent`, {
      params: { limit },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch recent notes');
  }

  /**
   * Get note statistics for a deal
   * @param dealId - Deal ID
   * @returns Note statistics
   */
  async getStats(dealId: string): Promise<NoteStatistics> {
    const response = await apiClient.get<ApiResponse<NoteStatistics>>(`${this.basePath}/stats`, {
      params: { dealId },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch note statistics');
  }

  /**
   * Pin a note (mark as important)
   * @param id - Note ID
   * @returns Updated note
   */
  async pin(id: string): Promise<Note> {
    const response = await apiClient.post<ApiResponse<Note>>(`${this.basePath}/${id}/pin`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to pin note');
  }

  /**
   * Unpin a note
   * @param id - Note ID
   * @returns Updated note
   */
  async unpin(id: string): Promise<Note> {
    const response = await apiClient.post<ApiResponse<Note>>(`${this.basePath}/${id}/unpin`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to unpin note');
  }

  /**
   * Add tags to a note
   * @param id - Note ID
   * @param tags - Array of tags to add
   * @returns Updated note
   */
  async addTags(id: string, tags: string[]): Promise<Note> {
    const response = await apiClient.post<ApiResponse<Note>>(`${this.basePath}/${id}/tags`, {
      tags,
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to add tags to note');
  }

  /**
   * Remove tags from a note
   * @param id - Note ID
   * @param tags - Array of tags to remove
   * @returns Updated note
   */
  async removeTags(id: string, tags: string[]): Promise<Note> {
    const response = await apiClient.delete<ApiResponse<Note>>(`${this.basePath}/${id}/tags`, {
      data: { tags },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to remove tags from note');
  }

  /**
   * Update note sentiment
   * @param id - Note ID
   * @param sentiment - Sentiment value
   * @returns Updated note
   */
  async updateSentiment(id: string, sentiment: 'positive' | 'neutral' | 'negative'): Promise<Note> {
    const note = await this.getById(id);

    return this.update(id, {
      metadata: {
        ...note.metadata,
        sentiment,
      },
    });
  }

  /**
   * Bulk delete notes
   * @param ids - Array of note IDs to delete
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/bulk`, {
      data: { ids },
    });
  }

  /**
   * Export notes for a deal
   * @param dealId - Deal ID
   * @param format - Export format (json, csv, pdf)
   * @returns Exported file blob
   */
  async export(dealId: string, format: 'json' | 'csv' | 'pdf' = 'json'): Promise<Blob> {
    const response = await apiClient.get(`${this.basePath}/export`, {
      params: { dealId, format },
      responseType: 'blob',
    });

    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const noteService = new NoteService();

/**
 * Export default
 */
export default noteService;
